import { SelectChangeEvent, Stack } from "@mui/material"
import {
    Button,
    Chip,
    DialogHandler,
    EEditorType,
    EnhancedFormModel,
    EPageBranding,
    fetchRoute,
    FormsFactory,
    FullBar,
    GenericWrapperContext,
    getBrandUrl,
    Icon,
    InlineTextEdit,
    ListModel,
    ListsFactory,
    LoadingContainer,
    Modal,
    PageModel,
    PagesFactory,
    ResourceEdit,
    Typography
} from "@cakemail-org/ui-components-v2"
import { useTranslation } from "react-i18next"
import { useContext, useState, useEffect, ChangeEvent, useRef } from "react"
import { ERoutes } from "../../../utils/url"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import { useLocation, useParams } from "react-router-dom"
import BeeEditor from "../../../components/beeEditor"
import { ResourcePreview } from "../../../components/Resources/preview"
import "../styles.scss"
import { AddTagsButton } from "../../../components/Tags"
import { TEditPage } from "../types"
import { logError, setDataInPath } from "../../../utils/data"
import { ELogErrorMsg } from "../../../utils/data/types"
import { FromCommonResourceEdit } from "../../forms/components/commonResource"
import { buildDefaultUrls, updateFormLayoutOptions } from "../../forms/utils"
import ResourceDetailContainer from "../../../components/contentTemplate/resourceDetailContainer"
import { PageSeoEdit } from "../components/pageSeo"
import { PageSocialShareEdit } from "../components/socialShare"
import { PageSlugFaviconEdit } from "../components/slugFavicon"
import { PageTrackingEdit } from "../components/tracking"
import { FileManagerOverlay } from "../../../components/beeEditor/fileManagerOverlay"
import { TFileManagerFile } from "../../../components/beeEditor/types"


export default function PageDetails({ }: TEditPage) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()
    const { pageId } = useParams()

    const location = useLocation()

    const [isEditing, setIsEditing] = useState<boolean>(location.pathname.split('/').pop() === 'edit')
    const [fileManagerOpened, setFileManagerOpened] = useState<boolean>(false)
    const [canLoadFileManager, setCanLoadFileManager] = useState<boolean>(false)

    const fileManagerActionRef = useRef<((file: TFileManagerFile) => void) | undefined>(undefined)

    const [page, setPage] = useState<PageModel | undefined>(undefined)
    const [form, setForm] = useState<EnhancedFormModel | undefined>(undefined)

    const [selectedList, setSelectedList] = useState<ListModel>()
    const [previewContent, setPreviewContent] = useState<string | undefined>(undefined)

    const [loading, setLoading] = useState<boolean>(false)
    const [renderLoading, setRenderLoading] = useState<boolean>(false)

    useEffect(() => {
        fetchPage().then((response) => {
            if (response?.list_id) {
                ListsFactory.get({ id: response.list_id })
                    .then((list) => {
                        setSelectedList(list)
                    })
            }
        })
    }, [pageId])

    async function fetchPage() {
        setLoading(true)
        return PagesFactory.get({ id: pageId || "" })
            .then(async (page) => {
                if (page?.form_id) {
                    await fetchForm(page.form_id).then(async (form) => {
                        page.content.json = await updateFormLayoutOptions({
                            content: page.content.json,
                            listId: page.list_id,
                            submissionUrl: form.submission_url,
                            recaptchaSiteKey: form.recaptcha.site_key
                        })
                    })
                }
                setPage(page)
                renderPage(page)
                return page
            }).catch(() => {
                DialogHandler.show({
                    type: "warning",
                    title: t("general-error-msg")
                })
                window.location.href = "#" + fetchRoute({ route: ERoutes.pages })
            })
            .finally(() => {
                setLoading(false)
            })
    }

    async function fetchForm(id: string): Promise<EnhancedFormModel> {
        return FormsFactory.get({ id: id })
            .then((form) => {
                setForm(form as EnhancedFormModel)
                return form as EnhancedFormModel
            })
    }

    async function renderPage(cPage?: PageModel) {
        setRenderLoading(true)
        const pageToRender = cPage || page
        if (pageToRender) {
            pageToRender.render().then((content) => {
                setPreviewContent(content)
            }).catch(() => {
                setPreviewContent("")
            }).finally(() => {
                setRenderLoading(false)
            })
        }
    }

    function handlePublish(e: React.MouseEvent<HTMLButtonElement>, btn: TButtonRef) {
        if (page) {
            validateFormUrls()
            page.publish().then((page) => {
                DialogHandler.show({
                    type: "success",
                    title: t("general-published")
                })
                setPage(page)
            }).catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action")
                })
                logError(ELogErrorMsg.ErrorPublishingPage, error)
            }).finally(() => {
                btn.reset()
            })
        }
    }

    async function handleBrandingToggle(): Promise<any> {
        if (page) {
            return savePage({
                dataToUpdate: {
                    branding: page.branding === EPageBranding.smart ? EPageBranding.basic : EPageBranding.smart
                },
                render: true
            }).then((data) => {
                if (data) {
                    return data?.content?.json || null
                }
            })
        }
    }


    function handleInlineFieldConfirm({ name, value }: { name?: string, value?: string }) {
        if (page) {
            savePage({ dataToUpdate: { [name as "name"]: value || "" } })
        }
    }


    function handleTag(tag: string, btn: TButtonRef) {
        if (page) {
            savePage({ dataToUpdate: { tags: [...(page.tags || []), tag] } }).finally(() => {
                btn.reset()
            })
        }
    }

    function handleUntag(tag: string, btn: TButtonRef) {
        if (page) {
            savePage({ dataToUpdate: { tags: (page.tags || []).filter((t) => t !== tag) } }).finally(() => {
                btn.reset()
            })
        }
    }

    function handleDDChanges(event: SelectChangeEvent<string>) {
        const { name, value } = event.target

        if (!page) return

        const dataToUpdate = setDataInPath({ name, value, data: page, merge: true })

        setPage(new PageModel({ ...page, ...dataToUpdate }))
        savePage({ dataToUpdate, render: false })
    }


    async function handleOnEditorSave(jsonFile: string, htmlFile: string): Promise<any> {
        if (page) {
            return savePage({
                dataToUpdate: {
                    content: {
                        json: JSON.parse(jsonFile)
                    }
                },
                render: true
            })
        }
    }

    function handleOnClose() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.pages })
    }

    function handlePageEdit() {
        setIsEditing(true)
        window.location.href = "#" + fetchRoute({ route: ERoutes.pageEdit, params: { pageId: pageId } })
    }

    function handleOnEditorClose() {
        setIsEditing(false)
        window.location.href = "#" + fetchRoute({ route: ERoutes.pageDetails, params: { pageId: pageId } })
    }

    function validateFormUrls() {
        if (!form) return
        const defaultUrls = buildDefaultUrls({ list: selectedList, account: genericWrapper.account, user: genericWrapper.user, form: form })
        if (form?.post_redirect_url === "" || form?.double_opt_in_redirect_url === "") {
            const formToSave: Partial<EnhancedFormModel> = {}
            if (form?.post_redirect_url === "") {
                formToSave.post_redirect_url = defaultUrls.post_redirect_url
            }
            if (form?.double_opt_in_redirect_url === "") {
                formToSave.double_opt_in_redirect_url = defaultUrls.double_opt_in_redirect_url
            }
            saveForm(formToSave)
        }
    }

    async function savePage({ dataToUpdate, render }: { dataToUpdate?: Partial<PageModel>, render?: boolean } = {}) {
        setLoading(true)
        const pageToSave = dataToUpdate || page
        if (pageToSave) {
            validateFormUrls()
            return page?.save(pageToSave).then((data) => {
                if (render) {
                    renderPage(data)
                }
                setPage(data)
                return data
            }).catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action")
                })
                logError(ELogErrorMsg.ErrorSavingPage, error)
            }).finally(() => {
                setLoading(false)
            })
        }
    }

    async function saveForm(dataToUpdate?: Partial<EnhancedFormModel | void>) {
        const formToSave = dataToUpdate || form
        if (formToSave) {
            return form?.save(formToSave).then((response) => {
                setForm(new EnhancedFormModel(response))
                return response
            }).catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action")
                })
                logError(ELogErrorMsg.ErrorSavingForm, error)
            })
        }
    }

    function handleTextFieldChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const { name, value } = event.target
        if (page) {
            const dataToUpdate = setDataInPath({ name, value, data: page, merge: true })
            setPage(new PageModel({ ...page, ...dataToUpdate }))
            savePage({ dataToUpdate, render: false })
        }
    }

    function handleFileInsert(file: any) {
        fileManagerActionRef.current && fileManagerActionRef.current(file)
    }

    function toggleFileManager(state?: boolean) {
        console.log("toggleFileManager", fileManagerOpened)
        setFileManagerOpened(state !== undefined ? state : !fileManagerOpened)
    }

    function handleOnEditorLoad() {
        // Fix duo editor load breaking loading of each one
        setCanLoadFileManager(true)
    }

    const isPublished = page?.published_on && page.published_on > 0
    const hasUnpublishedChanges = page && ((page?.published_on && page.published_on > 0 && page?.updated_on - 1 > page?.published_on) || page?.published_on == null)

    return <Modal className={"editPageModal"}
        open
        heading={{
            logoUrl: getBrandUrl({ brand: genericWrapper.partnerBrand, elKey: "logo" }),
            titleContent: [
                <Typography key="pageTitle" variant="title" color="body1.main">{t("general-page_one")}</Typography>,
                ...(page && [<Chip key="pageChip" label={t(isPublished ? "general-published" : "general-unpublished")} color={isPublished ? "secondary" : "primary"} />] || [])
            ],
            actions: [
                <Button
                    key="publish"
                    animated
                    disabled={!page || loading}
                    color="secondary"
                    variant='contained' startIcon={<Icon name={hasUnpublishedChanges ? "Publish18" : "Checkmark14"} />}
                    onClick={handlePublish}
                >
                    {t(hasUnpublishedChanges ? "general-publish" : "general-published")}
                </Button>
            ],
            closeBtnText: t("general-close"),
            closeBtnOnClick: handleOnClose
        }}
        content={
            <LoadingContainer loading={!page}>
                {page && <ResourceDetailContainer>
                    <Stack gap={6}>
                        <FullBar
                            left={
                                <Typography variant="title" color="primary.main">
                                    <InlineTextEdit
                                        name="name"
                                        tooltipTitle={t("pages-details-name-tooltip")}
                                        onConfirm={handleInlineFieldConfirm}
                                        defaultValue={page.name || ""}
                                        placeholder={t("general-untitled")}
                                    />
                                </Typography>

                            }
                            right={[
                                <Typography key="tagCount" variant="body1" className="semiBold">
                                    {page.tags?.length || 0} {t("general-tag", { count: page.tags?.length || 0 })}
                                </Typography>,
                                <AddTagsButton key="addTagsButton" tagList={page.tags || []} onTag={handleTag} onUntag={handleUntag} />
                            ]}
                        />

                        <Stack gap={6}>
                            <Typography variant="title" color="shade500.main">
                                {t("pages-details-settings-title")}
                            </Typography>

                            <PageSlugFaviconEdit
                                page={page}
                                savePage={savePage}
                                setPage={setPage}
                                toggleFileManager={toggleFileManager}
                                fileManagerActionRef={fileManagerActionRef}
                            />

                            <PageSeoEdit
                                page={page}
                                savePage={savePage}
                            />

                            <PageSocialShareEdit
                                page={page}
                                savePage={savePage}
                                setPage={setPage}
                                toggleFileManager={toggleFileManager}
                                fileManagerActionRef={fileManagerActionRef}
                            />

                            <PageTrackingEdit
                                page={page}
                                savePage={savePage}
                            />


                        </Stack>

                        {form && <Stack gap={6}>
                            <Typography variant="title" color="shade500.main">
                                {t("forms-details-settings-title")}
                            </Typography>

                            <ResourceEdit
                                title={t("general-audience_one")}
                                desc={t("form-audience-desc")}
                                support={<Typography variant="body1">{t("form-audience-support")} <b>'{selectedList?.name || ""}'</b></Typography>}
                            />

                            <FromCommonResourceEdit
                                form={form}
                                content={page?.content}
                                saveForm={saveForm}
                                saveContent={savePage}
                            />

                        </Stack>}

                    </Stack>

                    <ResourcePreview
                        loading={renderLoading || loading}
                        content={previewContent}
                        actions={[
                            <Button
                                key="edit"
                                color="primary"
                                variant='contained'
                                size="small"
                                onClick={handlePageEdit} >
                                {t("general-edit-page")}
                            </Button>
                        ]}
                    />


                    {canLoadFileManager && <FileManagerOverlay
                        hidden={!fileManagerOpened}
                        toggleFileManager={toggleFileManager}
                        handleFileInsert={handleFileInsert}
                    />
                    }

                    <BeeEditor
                        hidden={!isEditing}
                        titleContent={<Typography variant="title" >{t("general-edit-page")}</Typography>}
                        onClose={handleOnEditorClose}
                        editorType={EEditorType.pageDesigner}
                        initContent={page?.content?.json}
                        onSave={handleOnEditorSave}
                        onBrandingToggle={handleBrandingToggle}
                        onLoad={handleOnEditorLoad}
                        contentContext={{
                            list: selectedList,
                            formAction: form?.submission_url,
                            isSmart: page?.branding === "smart",
                            recaptchaSiteKey: form?.recaptcha?.site_key
                        }}
                    />


                </ResourceDetailContainer>
                }
            </ LoadingContainer>
        }
    />
}